/**
 * THE ONE WAY TO COMPARE TWO RECORD STAMPS -- an epoch parse, never a string comparison.
 *
 * WHY A STRING COMPARE IS WRONG HERE, measured rather than assumed. ISO-8601 instants sort lexicographically ONLY
 * when they share a precision, and this corpus does not: a census of `mission/` on 2026-08-29 found 2,682 stamps
 * written with milliseconds and 1,778 written to the second, out of 4,460. `'2026-08-29T04:00:00Z'` and
 * `'2026-08-29T04:00:00.000Z'` name the SAME instant and compare UNEQUAL as strings -- and worse, the seconds
 * spelling sorts AFTER the millisecond spelling of the same second, so a discharge keyed on `>` would report an act
 * as done purely because of how its author spelled the time.
 *
 * EVERY COMPARISON IN THE PIPELINE ROUTES THROUGH HERE, which is the point of the leaf. The failure this prevents is
 * not one wrong answer but a FAMILY of them -- finding discharge, latest-verdict selection, the settled predicate's
 * stamp ordering -- each of which would otherwise have been fixed independently, differently, and at three different
 * times. That is precisely the drift the knowledge corpus's other SSOTs exist to end.
 *
 * AN UNPARSEABLE STAMP IS NOT SILENTLY ZERO. `Date.parse` answers `NaN` for a malformed value, and every comparison
 * against `NaN` is false -- which would make a garbage stamp answer "not later than anything" and quietly discharge
 * nothing while looking decisive. So the parse is explicit and its failure is answerable: callers get `null` and
 * decide, rather than receiving a number that lies. The record-shape gate already refuses an unparseable stamp at the
 * author's seat, so this arm is a floor and not a workaround.
 *
 * HOME IS `const/` -- relocated out of `utils/knowledge-records/` on 2026-09-04, the relocation cure named by
 * QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001, whose `statusReason` clause (a) is the ONE home of the argument this leaf used to restate.
 *
 * THE PARSE ITSELF IS THE TREE'S ONE INSTANT READER, and three further names for it went with the 2026-09-04 fold:
 * dqParseIsoMs in const/dq-findings-core.ts, which served the data-quality arms and the dispatch ledger, and a
 * module-private instantMs in each of two census leaves. Every name is de-backticked because it is deleted -- a backtick
 * asserts a declaration a reader can chase. All four bodies agreed on every input and each could have been "fixed"
 * alone. {@link recordInstantMs} is the survivor because it is the only one of the four carrying prose citations
 * outside its own suite, so folding onto it strands no reader; the surviving BODY is the data-quality spelling, which
 * guards the string type and therefore answers null -- rather than a confident wrong instant -- for the non-string
 * cell a D1 row hands it.
 *
 * NO INSTANT READER OUTSIDE THIS LEAF IS CLAIMED AS AN ABSENCE, because an absence over NAMED readers misses the
 * INLINE spelling (a census over `const/s*`-`const/z*` on 2026-09-05 found a named twin and eight inline parse-plus-
 * finiteness call sites; `const/standard-write-elements.ts` and `const/whole-days-since.ts` among them call this
 * function). The claim is stated as a MEASURED figure
 * with its own falsifier: 86 bare `Date.parse(` call sites in non-test code tree-wide (2026-09-05), of which
 * `components/hooks/useAuctionCountdown.ts` spells this body exactly, and
 * `tools/data-quality/dq-evidence-partition-freshness.ts` adopts this function for its STRING arm, keeping only the
 * `instanceof Date` arm local because an R2 `uploaded` already carries the epoch and is not parsed at all. The rest of the 86
 * are unclassified and a sweep must read them rather than trust this sentence. THE MODULE-PRIVATE READERS in
 * tools/dev/issue-stage-latency-census.ts (eight call sites, plus the one `?? undefined` its narrower
 * `string | undefined` parameter forces) and in spa/mission/issue-investigation-fold.ts (three call sites, a
 * `string | null` parameter and a `Number.isNaN` test that is the same predicate as this one's `Number.isFinite`,
 * since `Date.parse` answers only NaN or a finite number) both call {@link recordInstantMs}. Both narrower parameter
 * types are subsumed by this leaf's `unknown` door, so each adoption widens what the caller may hand it and changes no
 * answer it gives.
 *
 * CONSTANT-INPUT `Date.parse(<known-good literal or const>)` IS OUT OF THIS CLASS, and the ground is that there is no
 * measurement gap to report. This function's whole contract is that an unreadable stamp becomes `null` for the caller
 * to answer for; a compile-time constant the author wrote and a suite pins CANNOT be unreadable, so every such
 * adoption would have to invent a `?? 0` (or a non-null assertion) for an arm that cannot be reached -- a fictional
 * default is worse than the parse it replaced, because it teaches a later reader that the input might be absent.
 * Fixture bases, test clock anchors and epoch constants therefore stay on the bare parse. FALSIFIER, so this is a
 * boundary and not a blanket excuse: a constant-input site that is NUMBER-typed downstream and THROWS (or refuses)
 * on an unreadable stamp is back in class -- it is asking the measurement question after all, and it takes this
 * function plus an explicit refusal rather than a bare parse.
 */

import { finiteNumberOrNull } from '@federa/const/number-guards';
import { isValidString } from '@federa/const/string-guards';

/**
 * Epoch milliseconds for an ISO-8601 instant, or null when the value is not a readable one. `null` is a MEASUREMENT
 * GAP the caller reports, never a zero to compute with.
 *
 * TAKES `unknown`, WHICH IS THE WHOLE REASON THE STRING GUARD IS HERE. Callers hand it D1 cells and JSON members, and
 * `Date.parse` COERCES its argument -- handed the number `123` it answers a confident instant in year 123 rather than
 * `NaN`. So the type is tested before the parse and a non-string is a gap, not a date.
 *
 * @runtime-derived-ssot-helper Layer-floor pinned: the stage predicates, the gated predicates and the finding discharge under `utils/knowledge-records/` order stamps through it and may not value-import a utils sibling (QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001, no allowlist).
 */
export function recordInstantMs(instant: unknown): number | null {
	if (!isValidString(instant)) return null;
	return finiteNumberOrNull(Date.parse(instant));
}

/**
 * Whether instant `at` falls at or after instant `since`, both read through {@link recordInstantMs}: an unreadable one
 * is never at or after anything, the fail-safe direction -- a settle test over an attempt and its ask, and a cutoff test
 * over a stamp and its cutoff, each spelled this conjunction.
 * @runtime-derived-ssot-helper (const-leaf-pinned): a projection over the same-file {@link recordInstantMs}.
 */
export function instantAtOrAfter(at: unknown, since: unknown): boolean {
	const atMs = recordInstantMs(at);
	const sinceMs = recordInstantMs(since);
	return atMs !== null && sinceMs !== null && atMs >= sinceMs;
}

/**
 * Whether `instant` is a string {@link recordInstantMs} reads -- the admission a door or gate takes before it keeps the
 * stamp verbatim, narrowed to `string` so the caller need not re-test the type it just proved.
 * @runtime-derived-ssot-helper Layer-floor pinned beside {@link recordInstantMs}, which it composes.
 */
export function isReadableInstant(instant: unknown): instant is string {
	return recordInstantMs(instant) !== null;
}

/**
 * The stamp verbatim when {@link isReadableInstant} admits it, else null -- the keep-or-drop a reader takes when an
 * unreadable instant means "no instant" rather than a refusal (the heartbeat's stamp members, an instrument reading's
 * `readAtIso`), which each spelled as the same ternary.
 * @runtime-derived-ssot-helper Layer-floor pinned beside {@link isReadableInstant}, which it composes.
 */
export function readableInstantOrNull(instant: unknown): string | null {
	return isReadableInstant(instant) ? instant : null;
}

/**
 * The instant as BOTH spellings a dated reading carries -- the string verbatim and its epoch milliseconds -- or null when
 * {@link recordInstantMs} cannot read it. For a reader that dates itself by the stamp (`at`) and ages it (`ms`), which
 * otherwise spelled the string guard and the NaN test side by side.
 * @runtime-derived-ssot-helper Layer-floor pinned beside {@link recordInstantMs}, which it composes.
 */
export function datedInstantOf(instant: unknown): { readonly iso: string; readonly ms: number } | null {
	const ms = recordInstantMs(instant);
	return ms === null || typeof instant !== 'string' ? null : { iso: instant, ms };
}

/**
 * True when `later` names a STRICTLY later instant than `earlier` -- the question every discharge arm asks ("did the
 * acting stamp post-date the verdict?").
 *
 * FALSE when either side is missing or unparseable, and the direction is deliberate: the callers all ask this to
 * decide whether an obligation has been DISCHARGED, so an unreadable answer must leave the obligation standing. A
 * `true` on unreadable input would clear work nobody did.
 *
 * @runtime-derived-ssot-helper Layer-floor pinned: the stage predicates, the gated predicates and the finding discharge under `utils/knowledge-records/` order stamps through it and may not value-import a utils sibling (QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001, no allowlist).
 */
export function isInstantAfter(later: string | null | undefined, earlier: string | null | undefined): boolean {
	const a = recordInstantMs(later);
	const b = recordInstantMs(earlier);
	return a !== null && b !== null && a > b;
}

/**
 * A TOTAL ORDER over (instant, id) -- instant first, id as the tie-break.
 *
 * THE TIE-BREAK IS NOT DECORATION. Two records may honestly share an instant (two verdicts authored in the same
 * second by the same batch), and "the latest" would then depend on the order a directory happened to list -- so the
 * same corpus would answer the same question differently on two machines, silently. An id comparison is arbitrary but
 * TOTAL and STABLE, which is the property a selection needs; a refusal was considered and declined, because a
 * predicate that throws on a legal corpus state turns a cosmetic collision into an outage.
 *
 * Unparseable instants sort FIRST (as `-Infinity`), so a malformed stamp can never win a "latest" selection -- the
 * same fail-safe direction {@link isInstantAfter} takes.
 *
 * @runtime-derived-ssot-helper Layer-floor pinned: the stage predicates, the gated predicates and the finding discharge under `utils/knowledge-records/` order stamps through it and may not value-import a utils sibling (QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001, no allowlist).
 */
export function compareRecordOrder(a: OrderedRecord, b: OrderedRecord): number {
	const at = orderInstantMs(a);
	const bt = orderInstantMs(b);
	return at === bt ? a.id.localeCompare(b.id) : at - bt;
}

/** What the total order reads off a record: its stamp and its id. */
type OrderedRecord = { authoredAt: string; id: string };

/** A record's place on the order's instant axis: its stamp, or `-Infinity` when the stamp is unreadable. */
function orderInstantMs(record: OrderedRecord): number {
	return recordInstantMs(record.authoredAt) ?? Number.NEGATIVE_INFINITY;
}

/**
 * The NEWEST of several stamps, answered as the stamp its author wrote -- or null when none is readable. For a reader
 * that dates a row by the latest of the acts it read (the countermeasure worklist's hold release, iss-96e84de5): an
 * absent or unparseable stamp dates nothing and can never win, the fail-safe direction {@link isInstantAfter} and
 * {@link compareRecordOrder} take, and of two spellings of one instant the first handed in is kept.
 *
 * @runtime-derived-ssot-helper Layer-floor pinned: the stage predicates, the gated predicates and the finding discharge under `utils/knowledge-records/` order stamps through it and may not value-import a utils sibling (QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001, no allowlist).
 */
export function newestInstantIso(instants: readonly (string | null | undefined)[]): string | null {
	let newest: { readonly ms: number; readonly iso: string } | null = null;
	for (const iso of instants) {
		const ms = recordInstantMs(iso);
		if (ms === null || typeof iso !== 'string') continue;
		if (newest === null || ms > newest.ms) newest = { ms, iso };
	}
	return newest?.iso ?? null;
}
